import { Entity, EntityAspect, EntityType, EntityManager, NavigationProperty, DataProperty } from 'breeze-client';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
// import { UtilityService } from '../utility.service';

export type Guid = string;
export { Entity };

export class Entities {
    static lookups: any;

    static registerEntityTypes(manager: EntityManager) {
        // manager.metadataStore.registerEntityTypeCtor('Project', Project);
        manager.metadataStore.registerEntityTypeCtor('Job', Job);
        manager.metadataStore.registerEntityTypeCtor('Employee', Employee);
        manager.metadataStore.registerEntityTypeCtor('TimeEntry', TimeEntry);
        manager.metadataStore.registerEntityTypeCtor('TimeCategory', TimeCategory);
        manager.metadataStore.registerEntityTypeCtor('Invoice', Invoice);
        manager.metadataStore.registerEntityTypeCtor('InvoiceLine', InvoiceLine);
        manager.metadataStore.registerEntityTypeCtor('PurchaseOrderLine', PurchaseOrderLine);
        manager.metadataStore.registerEntityTypeCtor('PurchaseOrderLineAttachment', PurchaseOrderLineAttachment);
        manager.metadataStore.registerEntityTypeCtor('User', User);
    }
}

class BaseEntity implements Entity {
    entityAspect: EntityAspect;
    entityType: EntityType;
}

class BaseGuidEntity extends BaseEntity {
    id: Guid;
}

class BaseIntEntity extends BaseEntity {
    id: number;
}

export class PurchaseOrderLine extends BaseIntEntity {
    jobId: number;
    job: Job;
    vendor: string;
    invoiceDate: Date;
    description: string;
    amount: number;
    created: Date;
    createdById: number;
    createdBy: User;
    deleted: Date;
    deletedById: number;
    deletedBy: User;
}

export class PurchaseOrderLineAttachment extends BaseIntEntity {
    name: string;
    fileName: string;
    purchaseOrderLineId: number;
    purchaseOrderLine: PurchaseOrderLine;
    created: Date;
    deleted: Date;
}

export class JobType extends BaseIntEntity {
    name: string;
    displayOrder?: number;
    deleted?: Date;
    color: string;
}

export class Job extends BaseIntEntity {
    jobNumber: any;
    jobTypeId: number;
    JobType: JobType;
    customerName: any;
    contactName: string;
    description: any;
    keyCode: any;
    jobAddress1: any;
    jobAddress2: any;
    jobCity: any;
    jobState: any;
    jobZip: any;
    isBillingAddress: boolean;
    note: any;
    highPriority: boolean;
    status: any;
    statusId: number;
    created: Date;
    createdById: number;
    completed: Date;
    completedById: number;
    homePhone: any;
    cellPhone: any;
    email: any;
    noEmailProvided: boolean;
    billingAddress1: any;
    billingAddress2: any;
    billingCity: any;
    billingState: any;
    billingZip: any;

    get displayName() {
        return `${this.jobNumber} - ${this.description}`;
    }

    get locationAddress() {
        return `${!!this.jobAddress1 ? this.jobAddress1 : ''} ${!!this.jobAddress2 ? this.jobAddress2 : ''} ${!!this.jobCity ? this.jobCity : ''} ${
            !!this.jobState ? this.jobState : ''
        }  ${!!this.jobZip ? this.jobZip : ''}`;
    }
}

export class JobStatus extends BaseIntEntity {
    static Statuses = {
        New: 1,
        Billing: 2,
        Closed: 3,
        Complete: 4
    };
}

export class User {
    id: number;
    userName: string;
    firstName: string;
    lastName: string;
    jwtToken?: string;
    employeeId: number;
    deleted: Date;
    roles: string[];
}


export class Employee extends BaseIntEntity {
    userId: number;
    password: string;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    cellPhone: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    startDate: Date;
    note: string;

    get displayName() {
        return `${this.firstName} ${this.lastName}`;
    }

    get displayAddress() {
        return `${!!this.address1 ? this.address1 : ''} ${!!this.address2 ? this.address2 : ''} ${!!this.city ? this.city : ''} ${!!this.state ? this.state : ''}  ${
            !!this.zip ? this.zip : ''
        }`;
    }
}

export class TimeEntry extends BaseIntEntity {
    job: Job;
    jobId: number;
    employeeId: number;
    employee: Employee;
    quantity: number;
    description: string;
    created: Date;
    deleted: Date;
    approved: Date;
    edited: Date;
    note: string;
}

export class TimeCategory extends BaseIntEntity {
    static Categories = {
        Hourly: 1,
        OvertimeHourly: 2,
        Holiday: 3,
        VacationHourly: 4,
        DoubleTimeHourly: 5,
        Premium: 6,
        OvertimePremium: 7
    };
    name: string;
    displayOrder?: number;
    deleted?: Date;
    isVisibleToEmployee: boolean;
    notesRequired: boolean;
}

export class Invoice extends BaseIntEntity {
    static Statuses = {
        New: 1,
        Invoiced: 2,
        Paid: 3
    };
    jobId: number;
    job: Job;
    created: Date;
    paid: Date;
    mailed: Date;
    poNumber: number;
    invoiceNumber: number;
    note: string;
    description: string;
    billingAddress1: string;
    billingAddress2: string;
    billingCity: string;
    billingState: string;
    billingZip: string;
    total: number;
    tax: number;
    code: Guid;
    invoiceLines: InvoiceLine[];
    processingFee: number;

    static getNextLineNumber(lines: any[]) {
        if (!lines || lines.length === 0) return 1;

        let lineNumber: any;
        lineNumber = _.maxBy(lines, 'lineNumber').lineNumber + 1;
        return lineNumber;
    }

    getNextLineNumber() {
        return Invoice.getNextLineNumber(this.invoiceLines);
    }

    get linesTotal() {
        let total = 0;
        if (!!this.invoiceLines && this.invoiceLines.length > 0)
            this.invoiceLines.forEach((line) => {
                total = total + line.lineTotal;
            });
        return total;
    }

    get invoiceAge() {
        return moment(new Date()).diff(moment(this.created), 'days');
    }
    get paidBoolean() {
        return !!this.paid;
    }
    get isMailed() {
        return !!this.mailed;
      }
}

export class InvoiceLine extends BaseIntEntity {
    static Statuses = {
        New: 1,
        Invoiced: 2,
        Paid: 3
    };
    invoiceId: number;
    invoice: Invoice;
    lineNumber: number;
    item: any;
    description: any;
    quantity: number;
    price: number;
    createdById: number;
    created: Date;
    deleted: Date;
    billingAddress1: string;
    billingAddress2: string;
    billingCity: string;
    billingState: string;
    billingZip: string;

    get lineTotal() {
        return this.quantity === 0 ? this.price : this.quantity * this.price;
    }
}

export class EmailMessage extends BaseIntEntity {
    invoiceId: number;
    invoice: Invoice;
    to: string;
    subject: string;
    body: string;
    created: Date;
}

export class Role extends BaseIntEntity {
    name: string;
}
